'use client';

import { useCallback, useEffect, useState } from 'react';

import type { UploadOptions, UploadState } from '@/types/upload';
import { getUploadState, subscribeToUploads } from '@/utils/uploadStateManager';

import { useGalleryUpload } from './useMediaGalleryMutations';

/**
 * Hook to subscribe to global upload state
 */
export function useUploadState() {
  const [state, setState] = useState<UploadState>(() => getUploadState());

  useEffect(() => {
    const unsubscribe = subscribeToUploads(setState);
    return unsubscribe;
  }, []);

  return state;
}

/**
 * Centralized gallery upload system hook
 * Handles all upload scenarios with consistent behavior
 */
export function useGalleryUploadSystem(options: UploadOptions = {}) {
  const {
    pollingStrategy = 'list',
    multiple: _multiple = true,
    enableScrollLock: _enableScrollLock = false,
    onUploadStart,
    onUploadSuccess,
    onUploadComplete,
    onUploadProgress,
  } = options;

  // Use the existing gallery upload hook
  const { uploadFile, isUploading, error, reset } = useGalleryUpload({
    onUploadSuccess,
    pollingStrategy,
  });

  // Batch upload function. Per-batch progress + completion UI is owned by the
  // GlobalUploadOverlay (subscribed to the global upload state); no duplicate
  // toasts here.
  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0)
      return;

    // Upload files sequentially to avoid overwhelming the server
    for (const file of files) {
      try {
        if (onUploadStart) {
          onUploadStart(file);
        }

        await uploadFile({
          file,
          onProgress: onUploadProgress,
        });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Call completion callback
    if (onUploadComplete) {
      await onUploadComplete();
    }
  }, [uploadFile, onUploadStart, onUploadComplete, onUploadProgress]);

  return {
    uploadFile,
    uploadFiles,
    isUploading,
    error,
    reset,
  };
}
